import React, { useState } from 'react';
import { Container } from 'react-bootstrap';

/* ---- "Added from the van. Live before you drive off." ----
   Interactive 3-step stepper paired with a phone mockup whose screen swaps
   by active step. Shared between the default and vertical landing pages so a
   copy edit lands on every page at once (see the landing copy-sync convention).
   Vertical-specific wording is overridable via props; defaults match the
   default (builder) landing page. */

interface Step {
  title: string;
  desc: string;
}

interface PhoneCopy {
  newTitle: string;   // screen title on step 1
  photoLabel: string; // monospace caption under the upload button
  nameLabel: string;  // field label on step 1
  nameValue: string;  // filled-in name (also the bold part of the step-3 toast)
  companyName: string; // brand name in the step-3 top bar
}

interface EaseStepperSectionProps {
  band?: 'light' | 'white';
  eyebrow?: string;
  title?: string;
  intro?: string;
  steps?: [Step, Step, Step];
  phone?: PhoneCopy;
  revealRef?: (el: HTMLDivElement | null) => void;
}

const DEFAULT_STEPS: [Step, Step, Step] = [
  { title: 'Snap a photo', desc: 'Straight from your camera roll — up to three per project.' },
  { title: 'Drop the pin', desc: 'Tap the map where the job was — or mark it approximate for privacy.' },
  { title: "It's live — everywhere", desc: 'On your public map and your website embed, instantly.' },
];

const DEFAULT_PHONE: PhoneCopy = {
  newTitle: 'New project',
  photoLabel: 'job photo',
  nameLabel: 'Project name',
  nameValue: 'Loft conversion — Hackney',
  companyName: 'Hartley Builds',
};

const EaseStepperSection: React.FC<EaseStepperSectionProps> = ({
  band = 'light',
  eyebrow = 'No skills required',
  title = 'Added from the van. Live before you drive off.',
  intro = "If you can post a photo, you can build this portfolio. Phone or computer — there's nothing to install and nothing to design.",
  steps = DEFAULT_STEPS,
  phone = DEFAULT_PHONE,
  revealRef,
}) => {
  const [phoneStep, setPhoneStep] = useState<0 | 1 | 2>(0);

  return (
    <section className={`landing-section${band === 'light' ? ' landing-section--light' : ''}`}>
      <Container>
        <div className="landing-ease landing-reveal" ref={revealRef}>
          {/* --- Left: copy + stepper --- */}
          <div className="landing-ease__text">
            <span className="landing-feature__eyebrow">{eyebrow}</span>
            <h2 className="landing-ease__title">{title}</h2>
            <p className="landing-ease__intro">{intro}</p>
            <div className="landing-ease__stepper">
              {steps.map((step, i) => {
                const active = phoneStep === i;
                return (
                  <button
                    key={i}
                    type="button"
                    className={`landing-ease__step${active ? ' landing-ease__step--active' : ''}`}
                    aria-pressed={active}
                    onClick={() => setPhoneStep(i as 0 | 1 | 2)}
                  >
                    <span className="landing-ease__step-num">{i + 1}</span>
                    <span className="landing-ease__step-body">
                      <span className="landing-ease__step-title">{step.title}</span>
                      <span className="landing-ease__step-desc">{step.desc}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* --- Right: phone mockup (decorative, no handlers) --- */}
          <div className="landing-phone" aria-hidden="true">
            <div className="landing-phone__screen">
              <div className="landing-phone__notch-row">
                <span className="landing-phone__notch" />
              </div>

              {phoneStep === 0 && (
                <div className="landing-phone__body">
                  <span className="landing-phone__title">{phone.newTitle}</span>
                  <div className="landing-phone__dropzone">
                    <span className="landing-phone__upload">↑</span>
                    <span className="landing-phone__mono">{phone.photoLabel}</span>
                  </div>
                  <div className="landing-phone__thumbs">
                    <span className="landing-phone__thumb" />
                    <span className="landing-phone__thumb" />
                    <span className="landing-phone__thumb landing-phone__thumb--add">+</span>
                  </div>
                  <span className="landing-phone__field-label">{phone.nameLabel}</span>
                  <span className="landing-phone__input">{phone.nameValue}</span>
                  <span className="landing-phone__cta landing-phone__cta--push">Next</span>
                </div>
              )}

              {phoneStep === 1 && (
                <div className="landing-phone__body">
                  <span className="landing-phone__title">Pin the location</span>
                  <div className="landing-phone__map">
                    <span className="landing-phone__pin" />
                    <span className="landing-phone__pin-shadow" />
                    <span className="landing-phone__hint">Tap anywhere to move the pin</span>
                  </div>
                  <label className="landing-phone__check">
                    <span className="landing-phone__checkbox" />
                    Location is approximate
                  </label>
                  <span className="landing-phone__cta">Publish</span>
                </div>
              )}

              {phoneStep === 2 && (
                <div className="landing-phone__live">
                  <div className="landing-phone__brandbar">
                    <span className="landing-phone__brand-dot" />
                    <span className="landing-phone__brand-name">{phone.companyName}</span>
                  </div>
                  <div className="landing-phone__livemap">
                    <span className="landing-map-dot" style={{ left: '24%', top: '30%' }} />
                    <span className="landing-map-dot" style={{ left: '62%', top: '56%' }} />
                    <span className="landing-map-dot landing-map-dot--hi" style={{ left: '44%', top: '40%' }} />
                    <div className="landing-phone__toast">
                      <span className="landing-phone__toast-check">✓</span>
                      <span className="landing-phone__toast-text">
                        <b>{phone.nameValue}</b> is live on your map &amp; website
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default EaseStepperSection;
